import { Form, Formik } from "formik";
import { FC } from "react";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { Button, Input, Loader, Typography } from "@/shared/ui";
import { useOnboardingStore } from "@/stores";

import { useFetchMe } from "./hooks/use-fetch-me.hook";
import styles from "./names.module.scss";
import { NamesTabFormScheme } from "./names.types";

const nameSchema = string().min(1, "Field is required").max(75, "Must be 75 characters or less");

const validationScheme = object({
  firstName: nameSchema,
  lastName: nameSchema,
});

const NamesTab: FC = () => {
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const firsName = useOnboardingStore((state) => state.userInfo.firstName);
  const lastName = useOnboardingStore((state) => state.userInfo.lastName);
  const { setCurrentStep, setInfo, addPassedStep } = useOnboardingStore((state) => state.actions);

  const { data, isPending } = useFetchMe();

  const onSubmitHandler = (data: NamesTabFormScheme) => {
    setInfo(data);
    setCurrentStep(currentStep + 1);
    addPassedStep(currentStep);
  };

  return (
    <main className={styles.namesTab}>
      <Typography semi variant="title-2">
        What’s your name ?
      </Typography>

      <Formik<NamesTabFormScheme>
        key={`${data?.firstName}-${data?.lastName}`} // for the restate initial values after fetchMe request
        initialValues={{ firstName: firsName || data?.firstName || "", lastName: lastName || data?.lastName || "" }}
        validationSchema={toFormikValidationSchema(validationScheme)}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={onSubmitHandler}
      >
        {({ values, errors, isValid, setFieldValue, validateField }) => (
          <Form className={styles.form}>
            <Input
              placeholder="First Name"
              variant={errors.firstName ? "error" : undefined}
              message={errors.firstName}
              value={values.firstName}
              endContent={isPending && <Loader size={20} border={3} />}
              onBlur={() => validateField("firstName")}
              onValueChange={async (value) => {
                const sanitizedValue = value.replace(/\s+/g, "");

                await setFieldValue("firstName", sanitizedValue);
                await validateField("firstName");
              }}
            />
            <Input
              placeholder="Last Name"
              variant={errors.lastName ? "error" : undefined}
              message={errors.lastName}
              value={values.lastName}
              endContent={isPending && <Loader size={20} border={3} />}
              onBlur={() => validateField("lastName")}
              onValueChange={async (value) => {
                await setFieldValue("lastName", value);
                await validateField("lastName");
              }}
            />

            <Button fullWidth size="big" type="submit" disabled={!isValid || Object.values(values).some((v) => !v)}>
              Continue
            </Button>
          </Form>
        )}
      </Formik>
    </main>
  );
};

export { NamesTab };
